/**
 * sbTheme — CodeMirror 6 theme using SecureBlob design tokens.
 *
 * Two theme variants: proportional (prose) and monospace.
 * Both use the app's CSS custom properties so they adapt to light/dark mode automatically.
 */
import { EditorView } from '@codemirror/view'
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language'
import { tags as t } from '@lezer/highlight'

// ── Shared base theme (layout, chrome, scrollbar) ──────────────

const baseTheme = EditorView.theme({
    '&': {
        flex: '1',
        minHeight: '0',
        backgroundColor: 'var(--color-bg)',
        color: 'var(--color-text-primary)',
    },
    '&.cm-focused': {
        outline: 'none',
    },
    '.cm-scroller': {
        overflow: 'auto',
    },
    '.cm-content': {
        padding: 'var(--sp-4)',
        caretColor: 'var(--color-accent)',
    },
    '.cm-line': {
        padding: '0',
    },
    '&.cm-focused .cm-cursor': {
        borderLeftColor: 'var(--color-accent)',
        borderLeftWidth: '2px',
    },
    '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
        backgroundColor: 'var(--color-accent-soft)',
    },
    '.cm-activeLine': {
        backgroundColor: 'transparent',
    },
    '.cm-gutters': {
        backgroundColor: 'var(--color-surface)',
        borderRight: '1px solid var(--color-border)',
        color: 'var(--color-text-tertiary)',
        transition: 'width 150ms ease-out',
    },
    '.cm-activeLineGutter': {
        backgroundColor: 'transparent',
    },
    // Line number gutter — quiet, subordinate to content
    '.cm-lineNumbers .cm-gutterElement': {
        fontFamily: 'var(--font-mono)',
        fontSize: '0.75em',
        color: 'var(--color-text-tertiary)',
        opacity: '0.6',
        padding: '0 8px 0 4px',
        minWidth: '2.5em',
        textAlign: 'right',
    },
    // Active line number — slightly bolder
    '.cm-lineNumbers .cm-activeLineGutter': {
        color: 'var(--color-text-secondary)',
        opacity: '1',
        fontWeight: '600',
    },
    // Thin scrollbar matching global style
    '.cm-scroller::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
    },
    '.cm-scroller::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '.cm-scroller::-webkit-scrollbar-thumb': {
        background: 'var(--color-border-strong)',
        borderRadius: '3px',
    },
})

// ── Markdown syntax highlighting ───────────────────────────────

const markdownHighlight = HighlightStyle.define([
    // Headings — accent colored, bold, scaled
    { tag: t.heading1, color: 'var(--color-accent)', fontWeight: '700', fontSize: '1.4em' },
    { tag: t.heading2, color: 'var(--color-accent)', fontWeight: '600', fontSize: '1.25em' },
    { tag: t.heading3, color: 'var(--color-accent)', fontWeight: '600', fontSize: '1.1em' },
    { tag: t.heading4, color: 'var(--color-accent)', fontWeight: '600' },
    { tag: t.heading5, color: 'var(--color-accent)', fontWeight: '600' },
    { tag: t.heading6, color: 'var(--color-accent)', fontWeight: '600' },

    // Emphasis
    { tag: t.emphasis, fontStyle: 'italic', color: 'var(--color-text-secondary)' },
    { tag: t.strong, fontWeight: '700' },
    { tag: t.strikethrough, textDecoration: 'line-through', color: 'var(--color-text-tertiary)' },

    // Links
    { tag: t.link, color: 'var(--color-accent)', textDecoration: 'underline' },
    { tag: t.url, color: 'var(--color-accent)', opacity: '0.7' },

    // Code (inline and fenced)
    {
        tag: t.monospace, fontFamily: 'var(--font-mono)', fontSize: '0.9em',
        backgroundColor: 'var(--color-surface-hover)', borderRadius: '3px', padding: '1px 4px'
    },

    // Quotes
    { tag: t.quote, color: 'var(--color-text-secondary)', fontStyle: 'italic' },

    // Lists — markers
    { tag: t.list, color: 'var(--color-accent)' },

    // Meta/markup chars (# **  ``` etc) — subtle
    { tag: t.processingInstruction, color: 'var(--color-text-tertiary)' },
    { tag: t.meta, color: 'var(--color-text-tertiary)' },

    // Content inside code blocks — use language-specific highlighting from language-data
    { tag: t.keyword, color: '#c678dd' },
    { tag: t.string, color: '#98c379' },
    { tag: t.number, color: '#d19a66' },
    { tag: t.bool, color: '#d19a66' },
    { tag: t.comment, color: 'var(--color-text-tertiary)', fontStyle: 'italic' },
    { tag: t.typeName, color: '#e5c07b' },
    { tag: t.definition(t.variableName), color: '#61afef' },
    { tag: t.variableName, color: 'var(--color-text-primary)' },
    { tag: t.function(t.variableName), color: '#61afef' },
    { tag: t.propertyName, color: '#e06c75' },
    { tag: t.operator, color: '#56b6c2' },
    { tag: t.punctuation, color: 'var(--color-text-tertiary)' },
])

// ── Font-specific themes ───────────────────────────────────────

export const sbThemeProportional = EditorView.theme({
    '.cm-scroller': {
        fontFamily: "var(--font-sans)",
        fontSize: '0.95rem',
        lineHeight: '1.65',
    },
})

export const sbThemeMono = EditorView.theme({
    '.cm-scroller': {
        fontFamily: "var(--font-mono)",
        fontSize: '0.875rem',
        lineHeight: '1.5',
    },
})

// Mobile font adjustment
const mobileTheme = EditorView.theme({
    '.cm-scroller': {
        fontSize: '1rem',
        padding: '0',
    },
    '.cm-content': {
        padding: 'var(--sp-3)',
    },
})

// ── Exports ────────────────────────────────────────────────────

/** Base theme + markdown syntax highlighting. Always include this. */
export const sbBaseExtensions = [
    baseTheme,
    syntaxHighlighting(markdownHighlight),
]

/**
 * Mobile-responsive adjustments (larger font on small screens).
 * Applied via CSS media query detection in the component.
 */
export const sbMobileTheme = mobileTheme
